/**
 * Types pour le Module Accessoires et Environnement - Section 5 du Cahier des Charges
 * Architecture modulaire avec sous-modules indépendants
 */

// Types de base communs
export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export interface DimensionsStructure {
  largeur: number;
  longueur: number;
  hauteur: number;
  centre?: Point3D;
  // Optionnel: informations sur le toit pour certains calculs d'éclairage
  toit?: {
    hauteurConvergence: number;
  };
}

// ====== TÂCHE 5.1: FIGURE HUMAINE 3D ======
export enum TypeFigureHumaine {
  HOMME_DEBOUT = 'homme-debout',
  FEMME_DEBOUT = 'femme-debout',
  ENFANT = 'enfant',
  PERSONNE_ASSISE = 'personne-assise'
}

// Éclairage local optionnel pour certains sous-modules (figure, végétation)
export interface ParametresEclairageLocal {
  actif?: boolean;
  type?: 'spot' | 'point' | 'diffus' | 'accent';
  intensite?: number;
  couleur?: string;
  portee?: number;
  cibles?: Array<'plantes' | 'arbustes' | 'decoration'>;
}

export interface ParametresFigureHumaine {
  actif: boolean;
  type: TypeFigureHumaine;
  position: Point3D;
  rotation: number; // Rotation en degrés
  echelle: number; // Facteur d'échelle (1.0 = taille normale)
  couleur: string;
  visible: boolean;
  // Optionnel: éclairage spécifique à la figure humaine
  eclairage?: ParametresEclairageLocal;
}

export interface ModeleFigureHumaine {
  id: string;
  type: TypeFigureHumaine;
  taille: number; // Taille en mètres
  largeur: number;
  profondeur: number;
  couleurDefaut: string;
}

// ====== TÂCHE 5.2: ÉCLAIRAGE AVANCÉ ======
// Seuls les éclairages 3D spécifiques sont supportés

export interface ParametresEclairage {
  actif: boolean;
  // Ambiance générale contrôlée par un slider continu :
  // 0.0 = nuit noire, 0.5 = crépuscule, 1.0 = petit matin/jour
  ambiance: number;
  lampeCentrale: {
    actif: boolean;
    intensite: number;
    couleur: string;
    taille: number;
    hauteur?: number; // 0 (sommet du toit) -> 1 (très bas)
    angle?: number;   // en radians, ouvre l’angle du cône du SpotLight
    fil?: boolean;    // afficher un câble de support
  };
  barresLEDVerticales: {
    actif: boolean;
    intensite: number;
    couleur: string;
    nombreLEDs: number;
  };
  barresLEDHorizontales: {
    actif: boolean;
    intensite: number;
    couleur: string;
    nombreLEDs: number;
  };
  spotsDirectionnels: {
    actif: boolean;
    nombre: number;
    intensite: number;
    couleur: string;
    angle: number;
  };
  guirlandeLED: {
    actif: boolean;
    intensite: number;
    couleur: string;
    nombreAmpoules: number;
    effet: 'fixe' | 'clignotant' | 'progressif';
  };
}

// ====== TÂCHE 5.3: VÉGÉTATION ======
export enum TypeVegetation {
  PLANTE_GRIMPANTE = 'plante-grimpante',
  ARBUSTE = 'arbuste',
  FLEUR = 'fleur',
  HERBE = 'herbe'
}

export interface ParametresVegetation {
  actif: boolean;
  plantesGrimpantes: {
    actif: boolean;
    surMontants: boolean;
    surTraverses: boolean;
    couleur: string;
    densite: number; // 0.1 à 1.0
    hauteur: number; // Pourcentage de la hauteur des montants
  };
  arbustes: {
    actif: boolean;
    positions: Point3D[];
    type: string;
    taille: number;
    couleur: string;
  };
  decoration: {
    actif: boolean;
    fleurs: boolean;
    herbe: boolean;
    couleurFleurs: string;
    couleurHerbe: string;
  };
  // Optionnel: éclairage spécifique à la végétation
  eclairage?: ParametresEclairageLocal;
}

// ====== TÂCHE 5.4: MOBILIER DE SCÈNE ======
export enum TypeMobilier {
  TABLE = 'table',
  CHAISE = 'chaise',
  CANAPE = 'canape',
  BANC = 'banc',
  PARASOL = 'parasol'
}

export interface ElementMobilier {
  id: string;
  type: TypeMobilier;
  position: Point3D;
  rotation: number;
  echelle: number;
  couleur: string;
  visible: boolean;
}

export interface ParametresMobilier {
  actif: boolean;
  elements: ElementMobilier[];
  modeles: {
    [TypeMobilier.TABLE]: {
      largeur: number;
      longueur: number;
      hauteur: number;
      couleur: string;
    };
    [TypeMobilier.CHAISE]: {
      largeur: number;
      profondeur: number;
      hauteur: number;
      hauteurAssise: number;
      couleur: string;
    };
    [TypeMobilier.CANAPE]: {
      largeur: number;
      profondeur: number;
      hauteur: number;
      couleur: string;
    };
    [TypeMobilier.BANC]: {
      largeur: number;
      profondeur: number;
      hauteur: number;
      couleur: string;
    };
    [TypeMobilier.PARASOL]: {
      diametre: number;
      hauteur: number;
      couleur: string;
    };
  };
}

// ====== STRUCTURE PRINCIPALE DU MODULE ======
export interface ParametresAccessoires {
  actif: boolean;
  figureHumaine: ParametresFigureHumaine;
  eclairage: ParametresEclairage;
  vegetation: ParametresVegetation;
  mobilier: ParametresMobilier;
}

export interface StructureAccessoires {
  parametres: ParametresAccessoires;
  figuresHumaines: ModeleFigureHumaine[];
  elementsMobilier: ElementMobilier[];
  donneesStructure: DimensionsStructure;
}

// État du module
export interface AccessoiresState {
  parametres: ParametresAccessoires;
  structure: StructureAccessoires | null;
  isCalculating: boolean;
  error: string | null;
}

// Configuration du module
export interface ConfigurationModuleAccessoires {
  nom: string;
  version: string;
  description: string;
  parametresDefaut: ParametresAccessoires;
  figuresDisponibles: ModeleFigureHumaine[];
  mobilierDefaut: ParametresMobilier['modeles'];
}

// Props pour les composants
export interface AccessoiresModuleProps {
  donneesStructure: DimensionsStructure;
  onAccessoiresChange?: (structure: StructureAccessoires | null) => void;
  onError?: (error: string) => void;
}

// Types pour les callbacks
export type CallbackAccessoiresChange = (structure: StructureAccessoires | null) => void;
export type CallbackFigureHumaineChange = (parametres: ParametresFigureHumaine) => void;
export type CallbackEclairageChange = (parametres: ParametresEclairage) => void;
export type CallbackVegetationChange = (parametres: ParametresVegetation) => void;
export type CallbackMobilierChange = (parametres: ParametresMobilier) => void;